/**
 * SidebarEvents – kompakte Karten für laufende Events (Boni und Event-Regionen) oben in der Sidebar.
 */
import { Component, For, Show, createSignal, onMount, onCleanup } from 'solid-js';
import { A } from '@solidjs/router';
import apiClient from '../utils/api';
import { useTranslation } from '../context/LanguageContext';

interface GameEvent { id: number; name: string; description?: string | null; event_type: string; starts_at: string; ends_at: string }

const TYPE_ICON: Record<string, string> = {
  bonus_money: '💰', bonus_points: '⭐', double_food_yield: '🍯', faster_breeding: '🐛', faster_research: '🔬',
  job_bonus: '💼', reduced_consumption: '🌿', queen_search_boost: '👑', custom: '🎉',
};
const TYPE_COLOR: Record<string, string> = {
  bonus_money: '245,158,11', bonus_points: '167,139,250', double_food_yield: '251,146,60', faster_breeding: '52,211,153',
  faster_research: '56,189,248', job_bonus: '251,191,36', reduced_consumption: '134,239,172', queen_search_boost: '249,168,212', custom: '232,121,249',
};
const REGION_ICON: Record<string, string> = {
  'Spukwald': '👻', 'Eishöhle': '❄️', 'Dachterrasse': '🎆', 'Kirschblütengarten': '🌸', 'Nebelwald': '🍂', 'Ostergarten': '🐣', 'Narrenstraße': '🎭',
};
const REGION_PREFIX = 'Neue Event-Region: ';

function remaining(endsAt: string): { text: string; urgent: boolean } {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return { text: '0 Min', urgent: true };
  const m = Math.floor(ms / 60000), d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60);
  if (d >= 1) return { text: `${d} T ${h} Std`, urgent: false };
  if (h >= 1) return { text: `${h} Std ${m % 60} Min`, urgent: h < 6 };
  return { text: `${m} Min`, urgent: true };
}

const SidebarEvents: Component<{ onNavigate?: () => void }> = (props) => {
  const { t } = useTranslation();
  const [events, setEvents] = createSignal<GameEvent[]>([]);
  const [tick, setTick] = createSignal(0);

  const load = async () => {
    try {
      const res = await apiClient.get<{ success: boolean; data: GameEvent[] }>('/game-events/active');
      if (res.data?.success) setEvents((res.data.data || []).filter((e) => e.event_type !== 'seasonal_effect'));
    } catch { /* keine Anzeige */ }
  };
  onMount(() => {
    load();
    const poll = setInterval(load, 5 * 60 * 1000);
    const clock = setInterval(() => setTick((n) => n + 1), 30000);
    onCleanup(() => { clearInterval(poll); clearInterval(clock); });
  });

  const view = (e: GameEvent) => {
    tick();
    const isRegion = e.id < 0;
    const name = isRegion ? e.name.replace(REGION_PREFIX, '') : e.name;
    const icon = isRegion ? (REGION_ICON[name] || '🗺️') : (TYPE_ICON[e.event_type] || '🎉');
    const color = isRegion ? '34,211,238' : (TYPE_COLOR[e.event_type] || '245,158,11');
    const total = new Date(e.ends_at).getTime() - new Date(e.starts_at).getTime();
    const used = total > 0 ? Math.min(1, Math.max(0, (Date.now() - new Date(e.starts_at).getTime()) / total)) : 0;
    return { isRegion, name, icon, color, link: isRegion || e.event_type === 'queen_search_boost', left: remaining(e.ends_at), pct: Math.round((1 - used) * 100) };
  };

  return (
    <Show when={events().length > 0}>
      <div class="sbe-wrap">
        <div class="sbe-head"><span class="sbe-live" />{t('sidebarEvents.title')}</div>
        <For each={events().slice(0, 3)}>
          {(e) => {
            const body = () => {
              const v = view(e);
              return (
                <>
                  <span class="sbe-icon">{v.icon}</span>
                  <span class="sbe-text">
                    <span class="sbe-name">{v.name}</span>
                    <span class="sbe-sub" classList={{ 'sbe-sub--urgent': v.left.urgent }}>
                      {v.isRegion ? t('sidebarEvents.region') : t('sidebarEvents.bonus')} · {t('sidebarEvents.left', { time: v.left.text })}
                    </span>
                  </span>
                  <Show when={v.link}><span class="sbe-go">›</span></Show>
                  <span class="sbe-bar"><span class="sbe-bar-fill" style={`width:${v.pct}%;`} /></span>
                </>
              );
            };
            const style = () => `--sbe:${view(e).color};`;
            const tip = () => (e.description || '').slice(0, 220);
            return (
              <Show when={view(e).link} fallback={<div class="sbe-card" style={style()} title={tip()}>{body()}</div>}>
                <A href="/queen-search" class="sbe-card sbe-card--link" style={style()} title={tip()} onClick={props.onNavigate}>{body()}</A>
              </Show>
            );
          }}
        </For>
      </div>
    </Show>
  );
};

export default SidebarEvents;
